import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Tag } from '../models/tag.entity'
import { TagsService } from '../tags.service'
import { Page } from '../../../models/util.model'
import { TagSearchParams } from '../models/tag-search.model'
import { TagsRepository } from '../tags.repository'

interface TagsListingState {
  filters?: TagSearchParams
  data?: Tag[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const TAGS_LISTING_INITIAL_STATE: TagsListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class TagsListingStore {
  constructor(
    private tagsService: TagsService,
    private tagsRepository: TagsRepository,
  ) {}

  private state = signal<TagsListingState>(TAGS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isErrored = computed(() => !!this.state().error)
  readonly tags = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly isFiltered = computed(() => !!this.state().filters)

  // Effects

  async search(params?: TagSearchParams, returnAll?: boolean): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.tagsService.search$(params, returnAll).subscribe({
        next: (data) => {
          resolve()
          this.setData(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async delete(tag: Tag) {
    return new Promise<boolean>((resolve) => {
      this.tagsRepository.alert('TAG_DELETION', tag).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.tagsService.delete$(tag._id).subscribe({
            next: () => {
              resolve(true)
              this.tagsRepository.notify({
                code: 'TAG_DELETED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        }
      })
    })
  }

  // Reducers

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: TagSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  addFilter(filter: TagSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: TagSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof TagSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  setData(data: Page<Tag>) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }
}
